import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import TransformIcon from '@mui/icons-material/Transform';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import StraightenIcon from '@mui/icons-material/Straighten';
import DownloadIcon from '@mui/icons-material/Download';
import GitHubIcon from '@mui/icons-material/GitHub';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { motion } from 'motion/react';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import Footer from '../Footer';
import {
  CLIPTOCAD_GITHUB,
  CLIPTOCAD_PLUGIN_REPO,
  CLIPTOCAD_QGIS_RANGE,
  CLIPTOCAD_TAGLINE,
  CLIPTOCAD_VERSION,
} from './cliptocadCopy';

const TOKENS = {
  primary: '#1E40AF',
  secondary: '#3B82F6',
  accent: '#D97706',
  accentGlow: 'rgba(217, 119, 6, 0.35)',
  bgDark: '#0B132B',
  bgSurface: '#0F172A',
  bgCard: 'rgba(15, 23, 42, 0.75)',
  border: 'rgba(219, 234, 254, 0.15)',
  borderActive: 'rgba(59, 130, 246, 0.5)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textHighlight: '#60A5FA',
  greenSignal: '#10B981',
};

const INSTALL_STEPS = [
  {
    n: '1',
    title: 'Install the plugin',
    body: 'In QGIS: Plugins → Manage and Install Plugins… → search ClipToCAD. Or copy a folder named exactly cliptocad into your QGIS profile plugins directory.',
  },
  {
    n: '2',
    title: 'Select features only',
    body: 'Use Select Feature(s). Nothing selected means nothing is copied — including a highlighted layer with an empty selection. Multi-layer selections are supported.',
  },
  {
    n: '3',
    title: 'Copy to clipboard',
    body: 'Click the ClipToCAD toolbar icon, Vector → ClipToCAD → Copy to CAD, or press Ctrl+Shift+C. A message bar confirms the selection that was copied.',
  },
  {
    n: '4',
    title: 'Paste in the command line',
    body: 'In AutoCAD press Esc twice, click the Command Line, then Ctrl+V. Jobs over 200 features or 5,000 vertices write cliptocad_last.scr — run SCRIPT and pick that file.',
  },
] as const;

const FEATURES = [
  {
    icon: <ContentCopyIcon sx={{ color: TOKENS.accent, fontSize: 28 }} />,
    title: 'Selection-only clipboard',
    body: 'Copies only selected features as ASCII AutoCAD command streams. No whole-layer dump, no DXF, no intermediate files.',
  },
  {
    icon: <LayersOutlinedIcon sx={{ color: '#A855F7', fontSize: 28 }} />,
    title: 'Multi-layer CAD layers',
    body: 'Selections across parcels, roads, and points emit a _-LAYER switch per source layer so they land on matching CAD layers in one paste.',
  },
  {
    icon: <ArchitectureIcon sx={{ color: TOKENS.secondary, fontSize: 28 }} />,
    title: 'Real World metres (LT-safe)',
    body: 'Pastes projected metres at true Easting/Northing. Command-line only — works on AutoCAD LT, BricsCAD, and ZWCAD. Geographic CRS is blocked.',
  },
  {
    icon: <StraightenIcon sx={{ color: TOKENS.greenSignal, fontSize: 28 }} />,
    title: 'Local AutoLISP + units',
    body: 'Click an insertion point and scale GIS metres to metric links, Gunter links, feet, yards, inches, or a custom factor. Full AutoCAD or LT 2024+.',
  },
  {
    icon: <TextFieldsIcon sx={{ color: '#EAB308', fontSize: 28 }} />,
    title: 'Auto parcel-ID labels',
    body: 'Each layer uses parcel_id or Sy.No when present. FID is never copied. Labels go on the CAD layer PARCEL_ID as _TEXT.',
  },
  {
    icon: <TransformIcon sx={{ color: TOKENS.textHighlight, fontSize: 28 }} />,
    title: 'Osnap-safe + SCRIPT',
    body: 'Real World paste sets OSNAPCOORD so typed vertices are not snapped. Large jobs write a .scr file instead of flooding the command line.',
  },
] as const;

const WHATS_NEW = [
  `${CLIPTOCAD_QGIS_RANGE} (Qt6 GeometryType and LayerType)`,
  'Selection-only copy from one or more layers',
  'Auto parcel-ID labels — FID is never used',
  'Local unit presets (links, feet, yards, inches)',
  'Osnap-safe paste and SCRIPT file for large jobs',
  'Resizable, scrollable Settings; Windows CRLF default',
] as const;

const STREAM_LINES = [
  { text: 'OSNAPCOORD', color: TOKENS.accent },
  { text: '1', color: TOKENS.textSecondary },
  { text: '_-LAYER', color: TOKENS.accent },
  { text: '_M', color: TOKENS.textSecondary },
  { text: 'LA_parcels', color: TOKENS.textHighlight },
  { text: '_PLINE', color: TOKENS.accent },
  { text: '534892.120,2048123.450', color: TOKENS.textPrimary },
  { text: '534950.880,2048200.120', color: TOKENS.textPrimary },
  { text: '535010.450,2048150.330', color: TOKENS.textPrimary },
  { text: '_C', color: TOKENS.accent },
  { text: '_-LAYER', color: TOKENS.accent },
  { text: '_M', color: TOKENS.textSecondary },
  { text: 'PARCEL_ID', color: TOKENS.textHighlight },
  { text: '_TEXT', color: TOKENS.accent },
  { text: '534921.180,2048157.890', color: TOKENS.textPrimary },
  { text: '142/3', color: TOKENS.greenSignal },
] as const;

const ClipToCADLandingPage = () => {
  return (
    <Box sx={{ ...publicPageSx, bgcolor: TOKENS.bgDark, color: TOKENS.textPrimary }}>
      <PublicSiteChrome active="cliptocad" subtitle="ClipToCAD 2.0.0" />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 8, md: 12 } }}>
        <Button
          component={RouterLink}
          to="/qgis-plugins"
          size="small"
          sx={{
            mb: 3,
            color: TOKENS.textSecondary,
            textTransform: 'none',
            fontWeight: 600,
            px: 0,
            '&:hover': { color: TOKENS.textHighlight, bgcolor: 'transparent' },
          }}
        >
          ← All QGIS Plugins
        </Button>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' },
            gap: 6,
            alignItems: 'center',
            mb: 10,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2, flexWrap: 'wrap' }}
              useFlexGap
            >
              <Chip
                label={`VERSION ${CLIPTOCAD_VERSION}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(217, 119, 6, 0.18)',
                  color: TOKENS.accent,
                  border: `1px solid ${TOKENS.accentGlow}`,
                  fontWeight: 800,
                  fontSize: 10,
                  letterSpacing: '0.08em',
                }}
              />
              <Chip
                label={CLIPTOCAD_QGIS_RANGE.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: 'rgba(59, 130, 246, 0.15)',
                  color: TOKENS.textHighlight,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
              <Chip
                label="SELECTION ONLY"
                size="small"
                sx={{
                  bgcolor: 'rgba(16, 185, 129, 0.15)',
                  color: TOKENS.greenSignal,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4rem' },
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.08,
                mb: 2.5,
              }}
            >
              ClipToCAD for QGIS
            </Typography>

            <Typography
              sx={{ color: TOKENS.textSecondary, fontSize: '1.15rem', lineHeight: 1.6, mb: 4 }}
            >
              {CLIPTOCAD_TAGLINE} Built for cadastral surveyors: selected parcels, matching CAD
              layers, and AutoCAD LT–safe Real World paste.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ flexWrap: 'wrap' }}
              useFlexGap
            >
              <Button
                component="a"
                href={CLIPTOCAD_PLUGIN_REPO}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                sx={{
                  bgcolor: TOKENS.accent,
                  color: '#FFF',
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: `0 8px 24px ${TOKENS.accentGlow}`,
                  '&:hover': { bgcolor: '#B45309' },
                }}
              >
                Install from QGIS Plugins
              </Button>

              <Button
                component="a"
                href={CLIPTOCAD_GITHUB}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="large"
                startIcon={<GitHubIcon />}
                sx={{
                  borderColor: TOKENS.border,
                  color: TOKENS.textPrimary,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': { borderColor: TOKENS.secondary, bgcolor: 'rgba(59, 130, 246, 0.1)' },
                }}
              >
                Source on GitHub
              </Button>

              <Button
                component={RouterLink}
                to="/cliptocad/issues"
                variant="outlined"
                size="large"
                startIcon={<HelpOutlineOutlinedIcon />}
                sx={{
                  borderColor: TOKENS.border,
                  color: TOKENS.textPrimary,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': { borderColor: TOKENS.secondary, bgcolor: 'rgba(59, 130, 246, 0.1)' },
                }}
              >
                Support &amp; FAQ
              </Button>
            </Stack>
          </motion.div>

          <Paper
            sx={{
              p: 4,
              bgcolor: TOKENS.bgCard,
              border: `1px solid ${TOKENS.borderActive}`,
              borderRadius: 3,
              boxShadow: `0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px ${TOKENS.accentGlow}`,
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    color: TOKENS.textHighlight,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  CLIPBOARD AUTOCAD STREAM
                </Typography>
                <Chip
                  label={`${CLIPTOCAD_VERSION} READY`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.2)',
                    color: TOKENS.greenSignal,
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                />
              </Stack>

              <Box
                sx={{
                  bgcolor: 'rgba(11, 19, 43, 0.9)',
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${TOKENS.border}`,
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
              >
                {STREAM_LINES.map((line, i) => (
                  <Typography
                    key={`${line.text}-${i}`}
                    sx={{ color: line.color, fontFamily: 'inherit', fontSize: 'inherit' }}
                  >
                    {line.text}
                  </Typography>
                ))}
                <Typography
                  sx={{ color: TOKENS.greenSignal, mt: 1.5, fontFamily: 'inherit', fontSize: 12 }}
                >
                  ✓ Selected features copied. Ctrl+V in the AutoCAD command line.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 800, mb: 3, textAlign: 'center' }}
        >
          What&apos;s new in {CLIPTOCAD_VERSION}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
            mb: 10,
          }}
        >
          {WHATS_NEW.map((item) => (
            <Paper
              key={item}
              elevation={0}
              sx={{
                p: 2.25,
                bgcolor: 'rgba(15, 23, 42, 0.55)',
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 18, color: TOKENS.greenSignal, mt: 0.2, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: 14, color: TOKENS.textPrimary, lineHeight: 1.5 }}>
                  {item}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>

        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 800, mb: 4, textAlign: 'center' }}
        >
          Cadastral CAD transfer
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 3,
            mb: 10,
          }}
        >
          {FEATURES.map((f) => (
            <Paper
              key={f.title}
              sx={{
                p: 3.5,
                bgcolor: 'rgba(15, 23, 42, 0.65)',
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 1.25,
                    bgcolor: 'rgba(30, 58, 138, 0.3)',
                    borderRadius: 2,
                    width: 'fit-content',
                  }}
                >
                  {f.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {f.title}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.9rem', color: TOKENS.textSecondary, lineHeight: 1.6 }}
                >
                  {f.body}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>

        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 800, mb: 4, textAlign: 'center' }}
        >
          Real World vs Local
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            mb: 10,
          }}
        >
          <Paper
            sx={{
              p: 3.5,
              bgcolor: TOKENS.bgCard,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: 3,
            }}
          >
            <Chip
              label="DEFAULT · LT SAFE"
              size="small"
              sx={{
                mb: 2,
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                color: TOKENS.greenSignal,
                fontWeight: 800,
                fontSize: 10,
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
              Real World coordinates
            </Typography>
            <Typography sx={{ color: TOKENS.textSecondary, fontSize: '0.95rem', lineHeight: 1.65 }}>
              Pastes UTM or other projected metres at their true Easting/Northing. Scale and unit
              controls are frozen. Uses only{' '}
              <Box component="span" sx={{ fontFamily: 'monospace', color: TOKENS.textHighlight }}>
                _PLINE
              </Box>
              {' / '}
              <Box component="span" sx={{ fontFamily: 'monospace', color: TOKENS.textHighlight }}>
                _POINT
              </Box>
              {' / '}
              <Box component="span" sx={{ fontFamily: 'monospace', color: TOKENS.textHighlight }}>
                _TEXT
              </Box>{' '}
              so AutoCAD LT can run the paste. Latitude/longitude CRS is refused.
            </Typography>
          </Paper>
          <Paper
            sx={{
              p: 3.5,
              bgcolor: TOKENS.bgCard,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: 3,
            }}
          >
            <Chip
              label="AUTOLISP · UNIT SCALE"
              size="small"
              sx={{
                mb: 2,
                bgcolor: 'rgba(217, 119, 6, 0.18)',
                color: TOKENS.accent,
                fontWeight: 800,
                fontSize: 10,
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
              Local insertion
            </Typography>
            <Typography sx={{ color: TOKENS.textSecondary, fontSize: '0.95rem', lineHeight: 1.65 }}>
              After Ctrl+V, AutoCAD asks for an insertion point. GIS metres are multiplied by your
              unit preset (metric links ×5, Gunter links, international or US survey feet, yards,
              inches) or a custom factor. Requires AutoLISP — full AutoCAD or LT 2024+.
            </Typography>
          </Paper>
        </Box>

        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 800, mb: 4, textAlign: 'center' }}
        >
          Installation &amp; usage
        </Typography>

        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 6 }}
        >
          {INSTALL_STEPS.map((s) => (
            <Paper
              key={s.n}
              sx={{
                p: 3.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: TOKENS.accent,
                    color: '#FFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {s.n}
                </Box>
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {s.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.9rem', color: TOKENS.textSecondary, lineHeight: 1.6 }}
                  >
                    {s.body}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: `1px solid ${TOKENS.borderActive}`,
            bgcolor: 'rgba(30, 58, 138, 0.2)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, mb: 0.75 }}
            >
              Folder name must be{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                cliptocad
              </Box>
            </Typography>
            <Typography
              sx={{ color: TOKENS.textSecondary, fontSize: 14, maxWidth: 640, lineHeight: 1.6 }}
            >
              Do not leave the plugin in a folder named cliptocad-2.0.0. QGIS imports the folder
              name as the package. Paste must go into the AutoCAD command line, not the drawing
              canvas. Need help? Read the FAQ.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/cliptocad/issues"
            variant="contained"
            sx={{
              flexShrink: 0,
              bgcolor: TOKENS.secondary,
              color: '#FFF',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              py: 1.15,
              '&:hover': { bgcolor: '#2563EB' },
            }}
          >
            Open FAQ &amp; issues
          </Button>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default ClipToCADLandingPage;
